export type Theme = { background: string; foreground: string; accent: string }

export const defaultTheme: Theme = {
  background: '#071417',
  foreground: '#c9f2ef',
  accent: '#65d5c3',
}

/**
 * Curated palettes. Every one of these clears WCAG AA (4.5:1) for foreground on
 * background and AA large (3:1) for accent on background — see theme.test.ts.
 */
export const presets = {
  mk69: defaultTheme,
  gruvbox: { background: '#1d2021', foreground: '#ebdbb2', accent: '#b8bb26' },
  nord: { background: '#2e3440', foreground: '#e5e9f0', accent: '#88c0d0' },
  dracula: { background: '#282a36', foreground: '#f8f8f2', accent: '#bd93f9' },
  matrix: { background: '#000000', foreground: '#b8f5c0', accent: '#39ff6a' },
  amber: { background: '#140d00', foreground: '#ffcb6b', accent: '#ff9f1c' },
  mono: { background: '#101010', foreground: '#e6e6e6', accent: '#a0a0a0' },
  paper: { background: '#f4f1ea', foreground: '#22201c', accent: '#8a5a20' },
} as const satisfies Record<string, Theme>

export type PresetName = keyof typeof presets

export const presetNames = Object.keys(presets) as PresetName[]

export const isPresetName = (value: string): value is PresetName => value in presets

export const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)

/* --------------------------------------------------------------- contrast */

const channel = (value: number): number =>
  value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4

/** Relative luminance per WCAG 2.x. */
export function luminance(hex: string): number {
  const parts = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255)
  return 0.2126 * channel(parts[0]) + 0.7152 * channel(parts[1]) + 0.0722 * channel(parts[2])
}

/** WCAG contrast ratio between two `#rrggbb` colours, 1–21. */
export function contrast(a: string, b: string): number {
  const first = luminance(a)
  const second = luminance(b)
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

export type ThemeAudit = { foreground: number; accent: number; readable: boolean }

/**
 * The theme editor lets people pick any colour, so the UI needs to say when a
 * combination has stopped being readable rather than silently shipping it.
 */
export const auditTheme = (theme: Theme): ThemeAudit => {
  const foreground = contrast(theme.foreground, theme.background)
  const accent = contrast(theme.accent, theme.background)
  return { foreground, accent, readable: foreground >= 4.5 && accent >= 3 }
}

/** True when the background is light, so the UI can flip `color-scheme`. */
export const isLight = (theme: Theme): boolean => luminance(theme.background) > 0.4

/**
 * Fixed status hues used by output spans (`ok`/`warn`/`err`/`num`/`str`).
 * Two sets, because saturated terminal colours vanish on a light background.
 */
const ANSI_DARK = { ok: '#4fd68b', warn: '#f2c14a', err: '#ff8a8a', num: '#c9a8ff', str: '#93c9ff' }
const ANSI_LIGHT = { ok: '#0b6b3c', warn: '#8a5d00', err: '#b3261e', num: '#5f3a9e', str: '#0b5394' }

export type AnsiPalette = typeof ANSI_DARK

export const ansiFor = (theme: Theme): AnsiPalette => (isLight(theme) ? ANSI_LIGHT : ANSI_DARK)

/** Writes the whole palette onto `:root` so plain CSS can consume it. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  root.style.setProperty('--background', theme.background)
  root.style.setProperty('--foreground', theme.foreground)
  root.style.setProperty('--accent', theme.accent)
  for (const [name, value] of Object.entries(ansiFor(theme))) {
    root.style.setProperty(`--ansi-${name}`, value)
  }
  root.style.colorScheme = isLight(theme) ? 'light' : 'dark'
}

/* ---------------------------------------------------------------- storage */

export const THEME_KEY = 'mk69-theme'

export const savedTheme = (): Theme => {
  try {
    const saved = JSON.parse(localStorage.getItem(THEME_KEY) ?? '{}') as Partial<Theme>
    return {
      background: isHexColor(saved.background) ? saved.background : defaultTheme.background,
      foreground: isHexColor(saved.foreground) ? saved.foreground : defaultTheme.foreground,
      accent: isHexColor(saved.accent) ? saved.accent : defaultTheme.accent,
    }
  } catch {
    return defaultTheme
  }
}

export const storeTheme = (theme: Theme): void => {
  try {
    localStorage.setItem(THEME_KEY, JSON.stringify(theme))
  } catch {
    // Private-mode quota errors must not take the terminal down.
  }
}
